import React, { useEffect, useState, useMemo } from "react";
import {
    Box, CssBaseline, Breadcrumbs, Link, Typography, Stack, Button,
    Paper, TextField, InputAdornment, IconButton,
    CircularProgress, Tooltip, Chip, Dialog, DialogTitle, DialogContent,
    DialogActions, Snackbar, Alert, Grid, Card, CardMedia, CardContent,
    CardActions, Divider, useMediaQuery, useTheme
} from "@mui/material";
import {
    Search, Add, Edit, DeleteOutline, WarningAmber, Campaign,
    CalendarToday, Visibility, NavigateNext, Close,
    FilterList
} from '@mui/icons-material';
import { useNavigate } from "react-router-dom";
import Header from "../../component/header/Header";

const colors = {
    pageBg: '#F8F9FA',
    sidebarBg: '#F1F2F6',
    filterBg: "#FFF9E6",
    accent: '#FFC107',
    accentDark: '#FFA000',
    tableHeader: '#FDF7E2',
    textMain: '#5D4037',
    white: '#FFFFFF'
};

const AnnouncementListScreen = () => {
    const navigation = useNavigate();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    // --- State ---
    const [announcementList, setAnnouncementList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [filterSearch, setFilterSearch] = useState("");

    // UI States
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
    const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
    const [tempDeleteData, setTempDeleteData] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    useEffect(() => {
        getAnnouncementList();
        localStorage.setItem("current_tab", "Announcement");
    }, []);

    const getAnnouncementList = async () => {
        setLoading(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/announcement`);
            const resJson = await response.json();
            if (resJson.status === 1 || resJson.data) {
                setAnnouncementList(resJson.data || []);
                console.log(resJson.data || []);
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Connection Error", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = (announcement) => {
        setSelectedAnnouncement(announcement);
        setOpenPreviewDialog(true);
    };

    const deleteAnnouncement = async (announcementId) => {
        setLoadingDelete(true);
        try {
            await fetch(`https://www.geniusmileniaeducation.link/db-api/announcement/${announcementId}`, {
                method: 'DELETE'
            });
            setSnackbar({ open: true, message: "Announcement deleted successfully", severity: "success" });
            setOpenDeleteDialog(false);
            getAnnouncementList();
        } catch (error) {
            setSnackbar({ open: true, message: "Delete failed", severity: "error" });
        } finally {
            setLoadingDelete(false);
        }
    };

    const filteredData = useMemo(() => {
        return announcementList.filter((item) =>
            item.title?.toLowerCase().includes(filterSearch.toLowerCase()) ||
            item.description?.toLowerCase().includes(filterSearch.toLowerCase())
        );
    }, [announcementList, filterSearch]);

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
            <CssBaseline />
            <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: colors.pageBg, m: 2, borderRadius: 5 }}>

                <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
                    <Link underline="hover" color="inherit" onClick={() => navigation("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
                    <Typography color="text.primary">Announcements</Typography>
                </Breadcrumbs>

                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} mb={4}>
                    <Header title="Announcement Post" subtitle="Keep everyone updated with the latest happenings" />
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigation("/Portal/Announcement/CreateAnnouncement")}
                        sx={{ bgcolor: colors.accent, color: 'black', borderRadius: 2, px: 3, '&:hover': { bgcolor: colors.accentDark }, height: '45px' }}
                    >
                        New Post
                    </Button>
                </Stack>

                <Paper elevation={0} sx={{ p: 2, mb: 4, bgcolor: colors.filterBg, borderRadius: 3, display: 'flex', gap: 2, alignItems: 'center', border: '1px solid #F0E6CC' }}>
                    <FilterList sx={{ color: colors.textMain }} />
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search for titles, events, or updates..."
                        value={filterSearch}
                        onChange={(e) => setFilterSearch(e.target.value)}
                        sx={{ bgcolor: 'white', borderRadius: 2 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: colors.accent }} />
                                </InputAdornment>
                            )
                        }}
                    />
                </Paper>

                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
                        <CircularProgress sx={{ color: colors.accent }} />
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {filteredData.map((row) => (
                            <Grid item xs={12} sm={6} lg={4} key={row.announcementId}>
                                <Card sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 4,
                                    border: '1px solid #EEE',
                                    transition: '0.3s',
                                    '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 20px rgba(0,0,0,0.05)' }
                                }}>
                                    <Box sx={{ position: 'relative' }}>
                                        <CardMedia
                                            component="img"
                                            height="180"
                                            image={row.imageUrl || "https://via.placeholder.com/400x200?text=No+Image+Available"}
                                            alt={row.title}
                                            sx={{ bgcolor: '#F5F5F5' }}
                                        />
                                        <Chip
                                            label={row.status === "A" ? "Active" : "Hidden"}
                                            size="small"
                                            sx={{
                                                position: 'absolute', top: 10, right: 10,
                                                bgcolor: row.status === "A" ? '#4CAF50' : '#757575',
                                                color: 'white', fontWeight: 'bold'
                                            }}
                                        />
                                    </Box>

                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                            <CalendarToday sx={{ fontSize: 14, color: colors.accent }} />
                                            <Typography variant="caption" color="text.secondary">{row.createdDate}</Typography>
                                        </Stack>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: colors.textMain, mb: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                            {row.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                            {row.description}
                                        </Typography>
                                    </CardContent>

                                    <Divider />

                                    <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
                                        <Box>
                                            <IconButton size="small" onClick={() => navigation(`/Portal/Announcement/EditAnnouncement/${row.announcementId}`)}><Edit fontSize="small" /></IconButton>
                                            <IconButton size="small" color="error" onClick={() => { setTempDeleteData(row); setOpenDeleteDialog(true); }}><DeleteOutline fontSize="small" /></IconButton>
                                        </Box>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<Visibility fontSize="small" />}
                                            onClick={() => handlePreview(row)}
                                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold', borderColor: '#EEE', color: colors.textMain }}
                                        >
                                            View Details
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>

            {/* --- PREVIEW DIALOG --- */}
            <Dialog
                open={openPreviewDialog}
                onClose={() => setOpenPreviewDialog(false)}
                fullScreen={fullScreen}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 5, overflow: 'hidden' } }}
            >
                <Box sx={{ position: 'relative' }}>
                    <IconButton
                        onClick={() => setOpenPreviewDialog(false)}
                        sx={{ position: 'absolute', right: 8, top: 8, zIndex: 10, bgcolor: 'rgba(255,255,255,0.7)', '&:hover': { bgcolor: 'white' } }}
                    >
                        <Close />
                    </IconButton>
                    <CardMedia
                        component="img"
                        height="300"
                        image={selectedAnnouncement?.imageUrl || "https://via.placeholder.com/600x300?text=Genius+Milenia"}
                        alt="Preview Image"
                    />
                </Box>
                <DialogContent sx={{ p: 4 }}>
                    <Stack direction="row" spacing={1} mb={2}>
                        <Chip label="Announcement" size="small" sx={{ bgcolor: colors.accent, fontWeight: 'bold' }} />
                        <Typography variant="caption" sx={{ alignSelf: 'center', color: 'text.secondary' }}>
                            Posted on {selectedAnnouncement?.createdDate}
                        </Typography>
                    </Stack>
                    <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ color: colors.textMain }}>
                        {selectedAnnouncement?.title}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body1" sx={{ lineHeight: 1.8, color: '#444', whiteSpace: 'pre-line' }}>
                        {selectedAnnouncement?.description}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, bgcolor: '#F9F9F9' }}>
                    <Button onClick={() => setOpenPreviewDialog(false)} variant="contained" sx={{ bgcolor: colors.accentDark, borderRadius: 2, px: 4 }}>
                        Close Preview
                    </Button>
                </DialogActions>
            </Dialog>

            {/* --- DELETE DIALOG --- */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} PaperProps={{ sx: { borderRadius: 4 } }}>
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <WarningAmber sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                    <DialogTitle sx={{ fontWeight: 'bold', p: 0 }}>Delete this post?</DialogTitle>
                    <DialogActions sx={{ justifyContent: 'center', gap: 2, mt: 2 }}>
                        <Button onClick={() => setOpenDeleteDialog(false)} color="inherit" variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
                        <Button variant="contained" color="error" onClick={() => deleteAnnouncement(tempDeleteData.announcementId)} disabled={loadingDelete} sx={{ borderRadius: 2 }}>
                            {loadingDelete ? <CircularProgress size={20} /> : "Delete"}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AnnouncementListScreen;